import { Icon } from "@blueprintjs/core";
import React, { useEffect, useRef, useState } from "react";

export interface ToasterMessage {
  id: string;
  message: string;
  type?: "info" | "success" | "warning" | "error";
  errorId?: string;
  duration?: number;
}

interface ToasterProps {
  message: ToasterMessage;
  onRemove: (id: string) => void;
  style?: React.CSSProperties;
}

const Toaster: React.FC<ToasterProps> = ({ message, onRemove, style }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const duration = message.duration || 15000; // 15 seconds default

  const handleRemove = React.useCallback(() => {
    setIsRemoving(true);
    setTimeout(() => {
      onRemove(message.id);
    }, 300); // Wait for fade out animation
  }, [onRemove, message.id]);

  const startTimer = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      handleRemove();
    }, duration);
  }, [duration, handleRemove]);

  const pauseTimer = React.useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = () => {
    setIsPaused(true);
    pauseTimer();
  };

  const handleMouseLeave = () => {
    setIsPaused(false);
    if (!isRemoving) {
      startTimer();
    }
  };

  const copyErrorId = async () => {
    if (message.errorId) {
      try {
        await navigator.clipboard.writeText(message.errorId);
        // You could add a small success indication here
      } catch (err) {
        console.error("Failed to copy error ID:", err);
      }
    }
  };

  const getIcon = () => {
    switch (message.type) {
      case "success":
        return "tick-circle";
      case "warning":
        return "warning-sign";
      case "error":
        return "error";
      case "info":
      default:
        return "info-sign";
    }
  };

  useEffect(() => {
    // Trigger entrance animation
    setIsVisible(true);
    startTimer();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [startTimer]);

  useEffect(() => {
    if (!isPaused && !isRemoving) {
      startTimer();
    }
  }, [isPaused, isRemoving, startTimer]);

  return (
    <div
      className={`toaster ${isVisible ? "toaster--visible" : ""} ${
        isRemoving ? "toaster--removing" : ""
      } toaster--${message.type || "info"}`}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="toaster__content">
        <div className="toaster__icon">
          <Icon icon={getIcon()} size={16} />
        </div>
        <div></div>
        <div className="toaster__message">
          {message.message}
          {message.errorId && (
            <button
              className="toaster__error-id-btn"
              onClick={copyErrorId}
              title="Copy Error ID"
            >
              <Icon icon="clipboard" size={14} />
              <span className="toaster__error-id">{message.errorId}</span>
            </button>
          )}
        </div>
        <div className="toaster__actions">
          <button
            className="toaster__close-btn"
            onClick={handleRemove}
            title="Close"
          >
            <Icon icon="cross" size={14} />
          </button>
        </div>
      </div>
      <div
        className={`toaster__progress ${isPaused ? "toaster__progress--paused" : ""}`}
        style={{
          animationDuration: `${duration}ms`,
        }}
      />
    </div>
  );
};

export default Toaster;
